import { useEffect, useMemo, useState } from "react";
import { RefreshCw, Save } from "lucide-react";
import { useQuoteReminderTemplates } from "@/lib/useQuoteReminderTemplates";
import {
  previewQuoteReminderText,
  QUOTE_REMINDER_VARIABLES,
  QuoteReminderTemplate,
  QuoteReminderTemplateUpdate,
} from "@/types/quoteReminderTemplates";

type TemplateDraft = {
  label: string;
  angle: string;
  subject: string;
  intro: string;
  highlight: string;
  body: string;
  high_value_threshold: string;
  high_value_highlight: string;
  high_value_body: string;
  whatsapp_enabled: boolean;
  whatsapp_body: string;
};

function toDraft(template: QuoteReminderTemplate): TemplateDraft {
  return {
    label: template.label,
    angle: template.angle,
    subject: template.subject,
    intro: template.intro,
    highlight: template.highlight,
    body: template.body,
    high_value_threshold: String(template.high_value_threshold ?? 1250),
    high_value_highlight: template.high_value_highlight ?? "",
    high_value_body: template.high_value_body ?? "",
    whatsapp_enabled: template.whatsapp_enabled,
    whatsapp_body: template.whatsapp_body ?? "",
  };
}

function buildUpdatePayload(draft: TemplateDraft): QuoteReminderTemplateUpdate {
  const threshold = Number(draft.high_value_threshold.replace(",", "."));
  return {
    label: draft.label.trim(),
    angle: draft.angle.trim(),
    subject: draft.subject.trim(),
    intro: draft.intro.trim(),
    highlight: draft.highlight.trim(),
    body: draft.body.trim(),
    high_value_threshold: Number.isFinite(threshold) ? threshold : 1250,
    high_value_highlight: draft.high_value_highlight.trim() || null,
    high_value_body: draft.high_value_body.trim() || null,
    whatsapp_enabled: draft.whatsapp_enabled,
    whatsapp_body: draft.whatsapp_body.trim() || null,
  };
}

function Field({
  label,
  value,
  onChange,
  multiline = false,
  rows = 3,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  multiline?: boolean;
  rows?: number;
}) {
  const commonClass =
    "mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500";

  return (
    <label className="block text-sm font-medium text-gray-700">
      {label}
      {multiline ? (
        <textarea
          value={value}
          rows={rows}
          onChange={(event) => onChange(event.target.value)}
          className={`${commonClass} resize-y leading-5`}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className={commonClass}
        />
      )}
    </label>
  );
}

export function QuoteReminderTemplatesPanel() {
  const { templates, loading, error, refresh, updateTemplate } = useQuoteReminderTemplates();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<TemplateDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const selectedTemplate = useMemo(() => {
    return templates.find((template) => template.id === selectedId) ?? templates[0] ?? null;
  }, [selectedId, templates]);

  useEffect(() => {
    if (!selectedId && templates.length > 0) {
      setSelectedId(templates[0].id);
    }
  }, [selectedId, templates]);

  useEffect(() => {
    if (selectedTemplate) {
      setDraft(toDraft(selectedTemplate));
    }
  }, [selectedTemplate]);

  const canSave =
    !!selectedTemplate &&
    !!draft &&
    draft.label.trim().length > 0 &&
    draft.angle.trim().length > 0 &&
    draft.subject.trim().length > 0 &&
    draft.intro.trim().length > 0 &&
    draft.highlight.trim().length > 0 &&
    draft.body.trim().length > 0;

  const handleSave = async () => {
    if (!selectedTemplate || !draft || !canSave) return;

    const threshold = Number(draft.high_value_threshold.replace(",", "."));
    if (!Number.isFinite(threshold) || threshold < 0) {
      setNotice({ type: "error", message: "Le seuil haut montant doit être un nombre positif." });
      return;
    }

    setSaving(true);
    setNotice(null);
    const result = await updateTemplate(selectedTemplate.id, buildUpdatePayload(draft));
    setSaving(false);

    if (result.success) {
      setNotice({ type: "success", message: "Template de relance enregistré." });
    } else {
      setNotice({ type: "error", message: result.error || "Erreur lors de l'enregistrement." });
    }
  };

  if (loading) {
    return <div className="py-12 text-center text-sm text-gray-500">Chargement des templates de relance...</div>;
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {error}
      </div>
    );
  }

  if (!selectedTemplate || !draft) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
        Aucun template de relance actif.
      </div>
    );
  }

  const previewSubject = previewQuoteReminderText(draft.subject);
  const previewIntro = previewQuoteReminderText(draft.intro);
  const previewHighlight = previewQuoteReminderText(draft.highlight);
  const previewBody = previewQuoteReminderText(draft.body);
  const previewHighValueHighlight = previewQuoteReminderText(draft.high_value_highlight);
  const previewHighValueBody = previewQuoteReminderText(draft.high_value_body);
  const previewWhatsapp = previewQuoteReminderText(draft.whatsapp_body);

  return (
    <div className="space-y-4">
      {notice && (
        <div
          className={`rounded-md border p-3 text-sm font-medium ${
            notice.type === "success"
              ? "border-green-200 bg-green-50 text-green-700"
              : "border-red-200 bg-red-50 text-red-700"
          }`}
        >
          {notice.message}
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="space-y-2">
          {templates.map((template) => (
            <button
              key={template.id}
              type="button"
              onClick={() => setSelectedId(template.id)}
              className={`w-full rounded-lg border p-4 text-left transition ${
                selectedTemplate.id === template.id
                  ? "border-gray-900 bg-gray-900 text-white"
                  : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              <span className="block text-xs font-semibold uppercase opacity-75">R{template.step}</span>
              <span className="mt-1 block text-sm font-semibold">{template.label}</span>
              <span className="mt-1 block text-xs opacity-80">{template.angle}</span>
              <span className="mt-2 block text-[11px] opacity-70">v{template.version}</span>
            </button>
          ))}
        </aside>

        <section className="rounded-lg border border-gray-200 bg-white">
          <div className="flex flex-col gap-3 border-b border-gray-100 p-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                R{selectedTemplate.step} - {selectedTemplate.label}
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                Clé {selectedTemplate.template_key} · version {selectedTemplate.version}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={refresh}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
                Recharger
              </button>
              <button
                type="button"
                disabled={!canSave || saving}
                onClick={handleSave}
                className="inline-flex items-center gap-2 rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
              >
                <Save className="h-4 w-4" aria-hidden="true" />
                {saving ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </div>

          <div className="grid gap-5 p-4 lg:grid-cols-[minmax(0,1fr)_360px]">
            <div className="space-y-5">
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Libellé" value={draft.label} onChange={(label) => setDraft({ ...draft, label })} />
                <Field label="Angle" value={draft.angle} onChange={(angle) => setDraft({ ...draft, angle })} />
              </div>

              <Field label="Sujet email" value={draft.subject} onChange={(subject) => setDraft({ ...draft, subject })} />
              <Field label="Introduction" value={draft.intro} onChange={(intro) => setDraft({ ...draft, intro })} multiline rows={3} />
              <Field label="Encadré principal" value={draft.highlight} onChange={(highlight) => setDraft({ ...draft, highlight })} multiline rows={3} />
              <Field label="Corps" value={draft.body} onChange={(body) => setDraft({ ...draft, body })} multiline rows={4} />

              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <div className="grid gap-4 md:grid-cols-[180px_minmax(0,1fr)]">
                  <Field
                    label="Seuil haut montant HT"
                    value={draft.high_value_threshold}
                    onChange={(high_value_threshold) => setDraft({ ...draft, high_value_threshold })}
                  />
                  <div className="text-sm text-amber-800">
                    Les champs ci-dessous remplacent l'encadré et le corps quand le devis atteint ce seuil.
                  </div>
                </div>
                <div className="mt-4 space-y-4">
                  <Field
                    label="Encadré haut montant"
                    value={draft.high_value_highlight}
                    onChange={(high_value_highlight) => setDraft({ ...draft, high_value_highlight })}
                    multiline
                    rows={3}
                  />
                  <Field
                    label="Corps haut montant"
                    value={draft.high_value_body}
                    onChange={(high_value_body) => setDraft({ ...draft, high_value_body })}
                    multiline
                    rows={3}
                  />
                </div>
              </div>

              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                <label className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
                  <input
                    type="checkbox"
                    checked={draft.whatsapp_enabled}
                    onChange={(event) => setDraft({ ...draft, whatsapp_enabled: event.target.checked })}
                    className="h-4 w-4 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  Envoyer automatiquement un WhatsApp sur cette relance
                </label>
                <div className="mt-4">
                  <Field
                    label="Message WhatsApp"
                    value={draft.whatsapp_body}
                    onChange={(whatsapp_body) => setDraft({ ...draft, whatsapp_body })}
                    multiline
                    rows={5}
                  />
                </div>
              </div>
            </div>

            <aside className="space-y-4">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <h3 className="text-sm font-semibold text-gray-900">Aperçu</h3>
                <p className="mt-3 text-xs font-medium uppercase text-gray-500">Sujet</p>
                <p className="mt-1 text-sm font-semibold text-gray-900">{previewSubject}</p>
                <p className="mt-4 text-xs font-medium uppercase text-gray-500">Email</p>
                <div className="mt-2 space-y-2 text-sm leading-5 text-gray-700">
                  <p>{previewIntro}</p>
                  <p className="rounded-md border-l-4 border-amber-400 bg-white p-3 font-medium">
                    {previewHighlight}
                  </p>
                  <p>{previewBody}</p>
                </div>
                {(draft.high_value_highlight.trim() || draft.high_value_body.trim()) && (
                  <>
                    <p className="mt-4 text-xs font-medium uppercase text-amber-700">Haut montant</p>
                    <div className="mt-2 space-y-2 text-sm leading-5 text-amber-900">
                      {draft.high_value_highlight.trim() && (
                        <p className="rounded-md border-l-4 border-amber-500 bg-white p-3 font-medium">
                          {previewHighValueHighlight}
                        </p>
                      )}
                      {draft.high_value_body.trim() && <p>{previewHighValueBody}</p>}
                    </div>
                  </>
                )}
                {draft.whatsapp_enabled && draft.whatsapp_body.trim() && (
                  <>
                    <p className="mt-4 text-xs font-medium uppercase text-emerald-700">WhatsApp</p>
                    <p className="mt-2 whitespace-pre-wrap rounded-md bg-white p-3 text-sm leading-5 text-emerald-900">
                      {previewWhatsapp}
                    </p>
                  </>
                )}
              </div>

              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <h3 className="text-sm font-semibold text-gray-900">Variables</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {QUOTE_REMINDER_VARIABLES.map((variable) => (
                    <code
                      key={variable.key}
                      className="rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700"
                      title={variable.example}
                    >
                      {`{${variable.key}}`}
                    </code>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </section>
      </div>
    </div>
  );
}
